import { Component, OnDestroy, OnInit } from "@angular/core";
import { Subject, takeUntil } from "rxjs";
import { Title } from "@angular/platform-browser";
import { MessageService } from "primeng/api";
import { ListEvents } from "src/app/model/response/list-events-response";
import { AuthService } from "src/app/service/auth.service";
import { EventsService } from "src/app/service/events.service";
import { GeolocationService } from "src/app/service/geolocation.service";
import { Platform } from "@angular/cdk/platform";
import { TranslocoService } from "@ngneat/transloco";
//import * as QRCode from "qrcode";

interface EventVisualData {
  event: ListEvents;
  buttonsOpacity: string;
  generalOpacity: string;
  infoOpacity: string;
  hasCheckin: boolean;
  disableButtons: boolean;
  showQRCode: boolean;
}

@Component({
  selector: "app-events",
  templateUrl: "./events.component.html",
  styleUrls: ["./events.component.css"],
})
export class EventsComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  eventList: ListEvents[] = [];
  eventVisualList: EventVisualData[] = [];

  scannerActive = false;
  userID = this.authService.getUserInformations();
  qrCode: string = "";
  currentEvent: any = null;
  infoEvent: any = null;
  buttonsOpacity: string = "1";
  generalOpacity: string = "1";
  infoOpacity: string = "1";
  colorGenerated: string = "";
  hasCheckin: boolean = false;
  modalShowInfoDialog: boolean = false;
  modalShowCheckin: boolean = false;

  isTypedQrcode: boolean = false;
  typedQrcode: string = "";
  eventName: string = "";
  eventDescription: string = "";

  confirmCheckinBtn: boolean = false;
  eventSelected: any = null;

  isLoading: boolean = false;
  isLoadingModalQr: boolean = false;
  
  modalShowQRCode: boolean = false;
  qrCodeImage: string = "";

  constructor(
    private eventService: EventsService,
    private authService: AuthService,
    private geolocationService: GeolocationService,
    private messageService: MessageService,
    private platform: Platform,
    private translocoService: TranslocoService,
    private titleService: Title
  ) {}

  ngOnInit() {
    this.setTitle();
    this.viewEventCheckin();
    this.pedirLocalizacao();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  setTitle() {
    this.translocoService
      .selectTranslate("title.events")
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => this.titleService.setTitle(value));
  }

  viewEventCheckin() {
    this.isLoading = true;
    const userID = this.authService.getUserInformations();

    Promise.all([
      this.eventService.getEvent("").toPromise(),
      this.eventService.getUserEvents(userID).toPromise(),
    ])
      .then(([eventResponse, userCheckinResponse]) => {
        const allEvents: ListEvents[] = eventResponse
          ? Object.values(eventResponse)
          : [];
        const userEventIDs = userCheckinResponse?.map(
          (event: any) => event.eventId
        );

        this.eventVisualList = allEvents.map((event) => {
          const hasCheckin: boolean = (userEventIDs ?? []).includes(event.id);
          let buttonsOpacity = "1";
          let generalOpacity = "1";
          let infoOpacity = "1";
          let disableButtons = false;
          const showQRCode = event.id === "7a0ee62b-33fd-4b5c-97d5-02c88f8a01b6";

          switch (event.status) {
            case "Concluido":
              if (hasCheckin) {
                buttonsOpacity = "0";
                infoOpacity = "0.6";
                generalOpacity = "1";
                disableButtons = true;
              } else {
                generalOpacity = "0.6";
                buttonsOpacity = "0.6";
                infoOpacity = "0.6";
                disableButtons = true;
              }
              break;

            case "Ativo":
              if (hasCheckin) {
                buttonsOpacity = "0";
                disableButtons = true;
              }
              break;
          }
          this.isLoading = false;
          return {
            event,
            buttonsOpacity,
            generalOpacity,
            infoOpacity,
            hasCheckin,
            disableButtons,
            showQRCode,
          };
        });
      })
      .catch((error) => {
        console.error("Erro ao carregar eventos:", error);
      });
  }

  startScanner(event: any) {
    const now = new Date();

    const start = new Date("2026-01-18T06:30:00");
    const end = new Date("2026-01-18T13:00:00");

    if (now < start) {
      this.showMessage(
        "warn",
        this.translocoService.translate("events.messages.checkInUnavailable"),
        this.translocoService.translate("events.messages.checkInNotAvailableYet")
      );
      return;
    }

    if (now > end) {
      this.showMessage(
        "warn",
        this.translocoService.translate("events.messages.checkInClosed"),
        this.translocoService.translate("events.messages.checkInPeriodEnded")
      );
      return;
    }

    if (this.platform.ANDROID || this.platform.IOS) {
    this.currentEvent = event;
    this.modalShowCheckin = true;
    } else {
      this.showMessage(
        "warn",
        this.translocoService.translate("events.messages.useMobileDevice"),
        this.translocoService.translate("events.messages.useMobileDeviceDesc")
      );
    }
  }

  handleQrCode(qrCode: string) {
    this.scannerActive = false;
    this.confirmCheckinBtn = true;

    let extractedId: string;
    if (this.isTypedQrcode) {
      extractedId = qrCode.trim().toLowerCase();
      if ((this.currentEvent.qrCode || "").trim().toLowerCase() !== extractedId) {
        this.showMessage("error", this.translocoService.translate("events.messages.invalidQRCode"), this.translocoService.translate("events.messages.invalidQRCodeDesc"));
        this.confirmCheckinBtn = false;
        return;
      }
    } else {
      extractedId = qrCode.split("/").pop() || "";
      if (extractedId !== this.currentEvent.qrCode) {
        this.showMessage("error", this.translocoService.translate("events.messages.invalidQRCode"), this.translocoService.translate("events.messages.invalidQRCodeDesc"));
        this.confirmCheckinBtn = false;
        return;
      }
    }

    // Valida localização para ambos (câmera e digitado)

    this.geolocationService
      .userInsideRay(
        this.currentEvent.latitude,
        this.currentEvent.longitude,
        this.currentEvent.radius
      )
      .then((result) => {
        if (result.success) {
          if (this.currentEvent.status !== "Ativo") {
            this.showMessage("error", this.translocoService.translate("events.messages.invalidQRCode"), this.translocoService.translate("events.messages.eventEnded"));
            this.confirmCheckinBtn = false;
          } else {
            this.performCheckin(extractedId);
          }
        } else {
          this.confirmCheckinBtn = false;
          this.showMessage(
            "error",
            this.translocoService.translate("events.messages.invalidQRCode"),
            this.translocoService.translate("events.messages.goToEventLocation")
          );
        }
      });
  }

  private performCheckin(extractedId: string) {
    this.isLoadingModalQr = true;
    this.eventService
      .checkinEvent(this.userID, this.currentEvent.id, extractedId)
      .subscribe({
        next: () => {
          this.isLoadingModalQr = false;
          this.isTypedQrcode = false;
          this.modalShowCheckin = false;
          this.confirmCheckinBtn = false;
          this.viewEventCheckin();
          this.showMessage("success", this.translocoService.translate("events.messages.checkInSuccess"), this.translocoService.translate("events.messages.checkInSuccessDesc"));
        },
        error: () => {
          this.confirmCheckinBtn = false;
          this.showMessage("error", this.translocoService.translate("events.messages.checkInError"), this.translocoService.translate("events.messages.checkInErrorDesc"));
        },
      });
  }

  backCheckin() {
    this.typedQrcode = "";
    this.isTypedQrcode = false;
    
  }
  closeModalCheckin() {
    this.typedQrcode = "";
    this.isTypedQrcode = false;
    this.modalShowCheckin = false;
    this.isLoadingModalQr = false;  
  }

  showInfoDialog(event: any) {
    this.modalShowInfoDialog = true;
    this.infoEvent = event;
    this.eventName = event.name;
    this.eventDescription = event.description;
    this.eventSelected = event;
  }

  initMap() {
    this.geolocationService.renderMap(
      "map",
      [this.eventSelected.latitude, this.eventSelected.longitude],
      this.eventSelected.radius
    );
  }

  private showMessage(
    severity: "success" | "error" | "info" | "warn",
    summary: string,
    detail: string
  ): void {
    this.messageService.clear();
    this.messageService.add({
      key: "toastMessages",
      severity,
      summary,
      detail,
    });
  }

  pedirLocalizacao() {
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        // console.log("Latitude:", pos.coords.latitude, "Longitude:", pos.coords.longitude);
      },
      (err) => {
        if (err.code === err.PERMISSION_DENIED) {
          this.showMessage(
            "warn",
            this.translocoService.translate("events.messages.useMobileDevice"),
            this.translocoService.translate("events.messages.locationDenied")
          );
        } else if (err.code === err.TIMEOUT) {
          this.showMessage(
            "warn",
            this.translocoService.translate("events.messages.useMobileDevice"),
            this.translocoService.translate("events.messages.locationTimeout")
          );
          this.pedirLocalizacao();
        }
      },
      { timeout: 10000 } // espera 10 segundos
    );
  }

  async showUserQRCode() {
    /*try {
      const userId = this.authService.getUserInformations();
      this.qrCodeImage = await QRCode.toDataURL(userId, {
        width: 300,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        }
      });
      this.modalShowQRCode = true;
    } catch (error) {
      console.error('Erro ao gerar QR Code:', error);
      this.showMessage('error', 'Erro', 'Falha ao gerar QR Code');
    }*/
  }
}
